import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import TaskForm from "./TaskForm";
import TaskDetailsPage from "./tasks/TaskDetailsPage";
import { useUser } from "../context/UserContext";
import CustomSelect from "./common/CustomSelect";
import { useNotification } from "../context/NotificationContext";
import { supabase } from "../supabaseClient";

const ALL_EXPORT_COLUMNS = [
  { id: "title", label: "Title" },
  { id: "assignee", label: "Assignee" },
  { id: "assignee_email", label: "Assignee Email" },
  { id: "assigned_by", label: "Assigned By" },
  { id: "start_date", label: "Start Date" },
  { id: "due_date", label: "Due Date" },
  { id: "priority", label: "Priority" },
  { id: "status", label: "Status" },
  { id: "budget", label: "Budget" },
  { id: "description", label: "Description" },
  { id: "hours", label: "Hours" },
  { id: "team", label: "Team" },
  { id: "sub_team", label: "Sub-Team" },
  { id: "created_at", label: "Created At" },
  { id: "updated_at", label: "Updated At" },
];

function TaskList({
  tasks = [],
  teams = [],
  subTeams = [],
  departments = [],
  users = [],
  userMemberships = [],
  onCreateTask,
  onUpdateTask,
  onDeleteTask,
  onRefreshUsers,
  onRefreshTasks,
  darkMode,
}) {
  const { showAlert } = useNotification();
  const { currentUser } = useUser();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [editingTask, setEditingTask] = useState(null);
  const [selectedTaskDetails, setSelectedTaskDetails] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteConfirmationTask, setDeleteConfirmationTask] = useState(null);
  const [attachmentsModalTask, setAttachmentsModalTask] = useState(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(
    ALL_EXPORT_COLUMNS.map((c) => c.id),
  );
  const [selectedImage, setSelectedImage] = useState(null);

  const isGlobalAdmin = currentUser?.role === "Global Admin";
  const isOperationalAdmin = currentUser?.role === "Operational Admin";
  const isDeptAdmin = currentUser?.role === "Department Admin";
  const isUser = currentUser?.role === "User";

  // Build assigned teams based on explicit team memberships AND department admin access
  const myAdminDeptIds = currentUser?.adminDeptIds || [];
  const myAllowedTeamIds = currentUser?.allowedTeamIds || [];
  
  const allAccessibleTeamIds = new Set([
    ...myAllowedTeamIds,
    ...teams.filter(t => myAdminDeptIds.includes(t.department_id)).map(t => t.id)
  ]);

  // Get unique teams the user has access to
  const assignedTeams = teams.filter(t => allAccessibleTeamIds.has(t.id));
  const totalAssignedTeams = assignedTeams.length;

  const [filterScope, setFilterScope] = useState(() => {
    if (isUser) return "my";
    if (isDeptAdmin && totalAssignedTeams <= 1) return "team";
    if (isDeptAdmin && totalAssignedTeams > 1) return "all";
    return "all"; // Global / Operational
  });

  const [filterStatus, setFilterStatus] = useState("all");
  const [showCompleted, setShowCompleted] = useState(false);
  const [advanceConfirmation, setAdvanceConfirmation] = useState(null);

  // Load filter when currentUser is available
  React.useEffect(() => {
    if (currentUser?.id) {
      try {
        const saved = localStorage.getItem(`taskListFilter_${currentUser.id}`);
        if (saved) {
          // Validate if saved filter is still allowed
          if (isUser && saved !== "my") {
            setFilterScope("my");
          } else if (isDeptAdmin && totalAssignedTeams <= 1 && saved !== "my" && saved !== "team") {
            setFilterScope("team");
          } else if (saved.startsWith("department:") && !isUser && (isGlobalAdmin || isOperationalAdmin || (isDeptAdmin && totalAssignedTeams > 1))) {
            setFilterScope("all");
          } else {
            setFilterScope(saved);
          }
        } else {
          // No saved filter, use default
          if (isUser) setFilterScope("my");
          else if (isDeptAdmin && totalAssignedTeams <= 1) setFilterScope("team");
          else setFilterScope("all");
        }
      } catch (err) {
        // Fallback default
        setFilterScope(isUser ? "my" : (isDeptAdmin && totalAssignedTeams <= 1 ? "team" : "all"));
      }
    }
  }, [currentUser, isUser, isDeptAdmin, totalAssignedTeams, isGlobalAdmin, isOperationalAdmin]);

  // Save filter changes
  React.useEffect(() => {
    if (currentUser?.id && filterScope) {
      try {
        localStorage.setItem(`taskListFilter_${currentUser.id}`, filterScope);
      } catch (err) {}
    }
  }, [filterScope, currentUser]);

  const canCreate =
    isGlobalAdmin || isOperationalAdmin || isDeptAdmin || isUser;
  const canExport = isGlobalAdmin || isOperationalAdmin || isDeptAdmin;



  const isTaskInMyTeam = (t) => {
    if (!t) return false;

    const allowedTeamIds = [
      currentUser?.team_id,
      ...(userMemberships || []).map((m) => m.team_id)
    ].filter(Boolean);

    if (allowedTeamIds.includes(t.team_id)) return true;

    const assigneeProfiles = getTaskAssigneeProfiles(t);

    return assigneeProfiles.some((u) => allowedTeamIds.includes(u.team_id));
  };

  const getCreatedByName = (task) => {
    const creator = users.find((u) => u.id === task?.created_by);
    return creator?.name || creator?.email || "—";
  };

  const getTaskAssignees = (task) => {
    if (!Array.isArray(task?.task_assignees)) return [];

    return task.task_assignees
      .map((a) => {
        const profile = Array.isArray(a.profiles) ? a.profiles[0] : a.profiles;
        const matchedUser = users.find((u) => u.id === a.user_id);
        const finalUser = profile || matchedUser;

        if (!finalUser) return null;

        return {
          id: finalUser.id || a.user_id,
          name: finalUser.name || "",
          email: finalUser.email || "",
          role: finalUser.role || "",
          photo_url: finalUser.photo_url || "",
          team_id: finalUser.team_id || "",
          department_id: finalUser.department_id || "",
          sub_team_ids: finalUser.sub_team_ids || [],
        };
      })
      .filter(Boolean);
  };

  const isAssignedToCurrentUser = (task) => {
    if (!currentUser) return false;

    return getTaskAssignees(task).some((a) => {
      return (
        a.id === currentUser.id ||
        (a.email &&
          currentUser.email &&
          a.email.toLowerCase() === currentUser.email.toLowerCase())
      );
    });
  };




  const getTaskAssigneeProfiles = (task) => {
    return getTaskAssignees(task);
  };

  const getAssigneeDisplay = (task) => {
    const assignees = getTaskAssignees(task);

    if (!assignees.length) return "Unassigned";

    return assignees
      .map((a) => a.name || a.email)
      .filter(Boolean)
      .join(", ");
  };

  const canEditTask = (t) => {
    if (isGlobalAdmin || isOperationalAdmin) return true;
    if (isDeptAdmin && isTaskInMyTeam(t)) return true;
    return false;
  };

  const getAttachments = (task) => {
    if (task.attachments && task.attachments.length > 0) {
      return task.attachments;
    }

    if (task.attachment_url) {
      return [{ name: "Attachment", url: task.attachment_url }];
    }

    return [];
  };

  const isImageUrl = (url) => {
    if (!url) return false;
    return url.match(/\.(jpg|jpeg|png|gif|webp)$/i);
  };

  const handleRowDelete = (task) => {
    if (isGlobalAdmin || isOperationalAdmin) {
      setDeleteConfirmationTask(task);
      return;
    }

    if (isDeptAdmin && isTaskInMyTeam(task)) {
      setDeleteConfirmationTask(task);
    }
  };

  const matchesSearch = (t) => {
    const q = (search || "").toLowerCase();
    if (!q) return true;

    const assigneeName = getAssigneeDisplay(t).toLowerCase();
    const title = (t.title || "").toLowerCase();

    return title.includes(q) || assigneeName.includes(q);
  };

  const formatDate = (val) => {
    if (!val) return "—";

    try {
      const dt = typeof val === "string" ? new Date(val) : val;
      if (Number.isNaN(dt.getTime())) return val;
      return dt.toISOString().slice(0, 10);
    } catch {
      return val;
    }
  };

  const uploadAttachmentFiles = async ({ files = [], folder, taskId }) => {
    const uploaded = [];

    for (const file of files || []) {
      if (!file) continue;

      if (file.url || file.publicUrl) {
        uploaded.push(file);
        continue;
      }

      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
      const filePath = `${folder}/${taskId}/${Date.now()}_${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from("attachments")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from("attachments")
        .getPublicUrl(filePath);

      uploaded.push({
        name: file.name,
        url: data.publicUrl,
        size: file.size,
        type: file.type || "file",
        path: filePath,
        uploaded_by: currentUser?.id || null,
        uploaded_at: new Date().toISOString(),
      });
    }

    return uploaded;
  };

  const addTaskChildrenAndNotify = async ({
    taskId,
    payload,
    cleanAssignedUsers,
    cleanUserCases,
  }) => {
    console.log("INSIDE addTaskChildrenAndNotify", {
      taskId,
      cleanAssignedUsers,
    });

    const { data: insertedAssignees, error: assigneeInsertError } =
      await supabase
        .from("task_assignees")
        .insert(
          cleanAssignedUsers.map((user) => ({
            task_id: taskId,
            user_id: user.id,
            assigned_by: currentUser?.id || null,
          }))
        )
        .select();

    if (assigneeInsertError) {
      console.error("task_assignees insert failed:", assigneeInsertError);
      showAlert("Task saved, but assignee insert failed: " + assigneeInsertError.message);
      return;
    }

    console.log("task_assignees inserted:", insertedAssignees);

    if (cleanUserCases.length > 0) {
      const userCaseRows = [];

      for (const uc of cleanUserCases) {
        const uploadedFiles = await uploadAttachmentFiles({
          files: uc.files || [],
          folder: "user-cases",
          taskId,
        });

        userCaseRows.push({
          task_id: taskId,
          title: uc.title || "Untitled User Story",
          description: uc.description || null,
          acceptance_criteria: uc.acceptanceCriteria || null,
          test_cases: (uc.testCases || []).map((t) => ({
            title: t,
            status: "Pending",
          })),
          test_cases_count: (uc.testCases || []).length,
          attachments: uploadedFiles,
          status: "Pending",
          created_by: currentUser?.id || null,
        });
      }

      if (userCaseRows.length > 0) {
        await supabase.from("task_user_cases").insert(userCaseRows);
      }
    }

    if (payload.issue_title || payload.issue_description) {
      const uploadedIssueFiles = await uploadAttachmentFiles({
        files: payload.issue_files || [],
        folder: "task-issues",
        taskId,
      });

      await supabase.from("task_issues").insert({
        task_id: taskId,
        title: payload.issue_title || "Untitled Issue",
        description: payload.issue_description || null,
        priority: payload.issue_priority || "Medium",
        status: "Open",
        reported_by: currentUser?.id || null,
        assigned_to: cleanAssignedUsers[0]?.id || null,
        attachments: uploadedIssueFiles,
      });
    }

    if (cleanAssignedUsers.length > 0) {
      const notificationRows = cleanAssignedUsers.map((user) => ({
        user_id: user.id,
        task_id: taskId,
        title: "Task assigned",
        message: payload.task_note || `You have been assigned "${payload.title}"`,
        type: "TASK_CREATED",
        action_role: "assignee",
        assignee_name: user.name || user.email,
        assigned_to: user.name || user.email,
        attachments: payload.attachments || [],
        is_read: false,
        is_dismissed: false,
      }));

      const { error: notificationError } = await supabase
        .from("notifications")
        .insert(notificationRows);

      if (notificationError) {
        console.error("Notification insert failed:", notificationError);
        showAlert("Task saved, but notification failed: " + notificationError.message);
      }

      const emailUsers = cleanAssignedUsers.filter((u) => u.email);

      console.log("emailUsers:", emailUsers);

      for (const user of emailUsers) {
        try {
          const response = await fetch("/api/email/task-created", {
            method: "POST",
            headers: { "Content-Type": "application/json", "x-internal-token": process.env.REACT_APP_INTERNAL_TOKEN || "" },
            body: JSON.stringify({
              users: [user],
              task: {
                id: taskId,
                title: payload.title,
                description: payload.task_note || payload.description,
                assigned_to: user.name || user.email,
                assigned_by: currentUser?.name || currentUser?.email || "N/A",
                start_date: payload.start_date,
                due_date: payload.due_date,
                priority: payload.priority,
                attachments: payload.attachments || [],
              },
              taskUrl: `${window.location.origin}/tasks/${taskId}`,
            }),
          });

          const result = await response.json();
          console.log("Task email response:", result);
        } catch (emailError) {
          console.error("Task email failed:", emailError);
        }
      }
    }
  };

  const handleSave = async (taskData) => {
    const now = new Date().toISOString();

    const cleanAssignedUsers = [
      ...new Map(
        (taskData.assigned_users || taskData.assignees || [])
          .map((u) => {
            if (u?.id) {
              const matched = users.find((user) => user.id === u.id);

              return {
                id: u.id,
                name: u.name || matched?.name || matched?.full_name || u.email,
                email: u.email || matched?.email || "",
              };
            }

            const matchedUser = users.find(
              (user) =>
                user.email?.toLowerCase() === u?.email?.toLowerCase() ||
                user.name?.toLowerCase() === u?.name?.toLowerCase()
            );

            return matchedUser
              ? {
                  id: matchedUser.id,
                  name: matchedUser.name || matchedUser.full_name,
                  email: matchedUser.email,
                }
              : null;
          })
          .filter((u) => u?.id)
          .map((u) => [u.id, u])
      ).values(),
    ];

    console.log("cleanAssignedUsers:", cleanAssignedUsers);

    if (cleanAssignedUsers.length === 0) {
      showAlert("Please select a valid assignee from the list.");
      return;
    }

    const cleanUserCases = (taskData.user_cases || []).filter(
      (uc) => uc.title || uc.description || uc.acceptanceCriteria,
    );

    if (editingTask) {
      const payload = {
        ...taskData,
        title: taskData.title,
        task_note: taskData.task_note || null,
        task_type: taskData.task_type || "regular",
        repeat_frequency: taskData.repeat_frequency || null,
        repeat_every: taskData.repeat_every || 1,
        repeat_start_date: taskData.repeat_start_date || null,
        repeat_end_date: taskData.repeat_end_date || null,
        repeat_occurrences: taskData.repeat_occurrences || null,
        repeat_days: taskData.repeat_days || [],
        loop_duration_value:
          taskData.loop_duration_value ?? taskData.loopDurationValue ?? 1,

        loop_duration_unit:
          taskData.loop_duration_unit ?? taskData.loopDurationUnit ?? "days",

        create_next_after_completion:
          taskData.create_next_after_completion ??
          taskData.createNextAfterCompletion ??
          false,

        description: taskData.description,
        assignees: taskData.assignees || [],
        assigned_users: taskData.assigned_users || taskData.assignees || [],
        start_date:
          taskData.start_date ||
          (taskData.startDate
            ? typeof taskData.startDate === "string"
              ? taskData.startDate.slice(0, 10)
              : new Date(taskData.startDate).toISOString().slice(0, 10)
            : null),
        due_date:
          taskData.due_date ||
          (taskData.dueDate
            ? typeof taskData.dueDate === "string"
              ? taskData.dueDate.slice(0, 10)
              : new Date(taskData.dueDate).toISOString().slice(0, 10)
            : null),
        priority: taskData.priority,
        status: taskData.status,
        budget: taskData.budget,
        hours: taskData.hours || 0,
        updated_at: now,
        attachment_url: taskData.attachment_url,
        attachments: taskData.attachments || [],
      };

      try {
        await onUpdateTask(editingTask.id, payload);

        await supabase
          .from("task_assignees")
          .delete()
          .eq("task_id", editingTask.id);

        if (cleanAssignedUsers.length > 0) {
          await supabase.from("task_assignees").insert(
            cleanAssignedUsers.map((user) => ({
              task_id: editingTask.id,
              user_id: user.id,
              assigned_by: currentUser?.id || null,
            })),
          );
        }

        await supabase
          .from("task_user_cases")
          .delete()
          .eq("task_id", editingTask.id);

        if (cleanUserCases.length > 0) {
          await supabase.from("task_user_cases").insert(
            cleanUserCases.map((uc) => ({
              task_id: editingTask.id,
              title: uc.title || "Untitled User Case",
              description: uc.description || null,
              acceptance_criteria: uc.acceptanceCriteria || null,
              status: uc.status || "Pending",
              created_by: currentUser?.id || null,
            })),
          );
        }

        if (onRefreshTasks) await onRefreshTasks();
      } catch (err) {
        console.error(err);
        showAlert("Failed to update task: " + (err?.message || err));
      } finally {
        setIsModalOpen(false);
        setEditingTask(null);
      }
    } else {
      const payload = {
        ...taskData,
        title: taskData.title || "Untitled",
        task_note: taskData.task_note || null,
        task_type: taskData.task_type || "regular",
        repeat_frequency: taskData.repeat_frequency || null,
        repeat_every: taskData.repeat_every || 1,
        repeat_start_date: taskData.repeat_start_date || null,
        repeat_end_date: taskData.repeat_end_date || null,
        repeat_occurrences: taskData.repeat_occurrences || null,
        repeat_days: taskData.repeat_days || [],
        loop_duration_value:
          taskData.loop_duration_value ?? taskData.loopDurationValue ?? 1,
        loop_duration_unit:
          taskData.loop_duration_unit ?? taskData.loopDurationUnit ?? "days",
        create_next_after_completion:
          taskData.create_next_after_completion ??
          taskData.createNextAfterCompletion ??
          false,
        description: taskData.description || null,
        start_date:
          taskData.start_date ||
          (taskData.startDate
            ? typeof taskData.startDate === "string"
              ? taskData.startDate.slice(0, 10)
              : new Date(taskData.startDate).toISOString().slice(0, 10)
            : null),
        due_date:
          taskData.due_date ||
          (taskData.dueDate
            ? typeof taskData.dueDate === "string"
              ? taskData.dueDate.slice(0, 10)
              : new Date(taskData.dueDate).toISOString().slice(0, 10)
            : null),
        priority: taskData.priority || "Medium",
        status: taskData.status !== undefined && taskData.status !== null ? taskData.status : "Todo",
        budget: taskData.budget || null,
        hours: taskData.hours || 0,
        created_at: now,
        updated_at: now,
        attachment_url: taskData.attachment_url || null,
        attachments: taskData.attachments || [],
        team_id: taskData.team_id || taskData.teamId || currentUser?.team_id || null,
        sub_team_id: taskData.sub_team_id || taskData.subTeamId || null,
        created_by: currentUser?.id || null,
      };

      try {
        const createdTask = await onCreateTask(payload);
        const createdTaskId = createdTask?.id;

        console.log("createdTask:", createdTask);
        console.log("createdTaskId:", createdTaskId);
        console.log("cleanAssignedUsers:", cleanAssignedUsers);

        if (!createdTaskId) {
          showAlert("Task saved failed: no task id returned.");
          return;
        }

        if (!cleanAssignedUsers.length) {
          showAlert("Task saved, but no assignee selected.");
          return;
        }

        await addTaskChildrenAndNotify({
          taskId: createdTaskId,
          payload,
          cleanAssignedUsers,
          cleanUserCases,
        });

        if (onRefreshTasks) await onRefreshTasks();
        setIsModalOpen(false);
      } catch (err) {
        console.error("Failed to create task:", err);
        showAlert("Failed to create task: " + (err?.message || err));
      }
    }
  };

  const handleRowStatusChange = async (task, newStatus) => {
    const now = new Date();
    const updates = { status: newStatus, updated_at: now.toISOString() };

    console.log("NEXT STATUS:", newStatus);
    console.log("TASK ID USED:", task.id);

    if (newStatus === "In Progress" && task.status !== "In Progress") {
      updates.timer_start = now.toISOString();
    } else if (task.status === "In Progress" && newStatus !== "In Progress") {
      if (task.timer_start) {
        const start = new Date(task.timer_start);
        const elapsedMs = now - start;
        const elapsedHours = elapsedMs / (1000 * 60 * 60);
        const currentHours = parseFloat(task.hours || 0);
        updates.hours = currentHours + elapsedHours;
        updates.timer_start = null;
      }
    }

    try {
      const { data, error } = await supabase
        .from("tasks")
        .update(updates)
        .eq("id", task.id)
        .select("id,title,status");

      console.log("SUPABASE STATUS UPDATE RESPONSE:", { data, error });

      if (error) {
        console.error("Failed to update task status:", error);
        alert(error.message);
        return false;
      }

      if (!data || data.length === 0) {
        console.warn("Supabase returned empty data. Check RLS or wrong task id.");
      }

      if (onRefreshTasks) {
        await onRefreshTasks();
      }
      return true;
    } catch (err) {
      console.error("Failed to update status in supabase:", err);
      showAlert("Failed to update status in database.");
      return false;
    }
  };

  const handleUserChangeStatus = (task) => {
    const currentStatus = task.status || "Todo";
    const nextStatus =
      currentStatus === "Todo" ? "In Progress" :
      currentStatus === "In Progress" ? "Completed" :
      currentStatus;
    
    setAdvanceConfirmation({ task, nextStatus });
  };

  const confirmAdvance = async () => {
    if (!advanceConfirmation) return;
    const task = advanceConfirmation.task;
    const nextStatus = advanceConfirmation.nextStatus;

    console.log("CONFIRM ADVANCE:", task.id, nextStatus);

    const { data, error } = await supabase
      .from("tasks")
      .update({ status: nextStatus })
      .eq("id", task.id)
      .select("id,title,status");

    console.log("ADVANCE DB RESULT:", { data, error });

    if (error) {
      alert("Advance failed: " + error.message);
      return;
    }

    if (!data || data.length === 0) {
      alert("Advance updated 0 rows. Check RLS policy or wrong task id.");
      return;
    }

    setAdvanceConfirmation(null);
    if (onRefreshTasks) {
      await onRefreshTasks();
    }
  };

  const cancelAdvance = () => {
    setAdvanceConfirmation(null);
  };

  const columns = [
    { key: "Todo", label: "To Do" },
    { key: "In Progress", label: "In Progress" },
    { key: "Completed", label: "Completed" },
  ];

  const confirmDelete = () => {
    if (deleteConfirmationTask) {
      onDeleteTask(deleteConfirmationTask.id, false);
      setDeleteConfirmationTask(null);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmationTask(null);
  };

  const rawFilteredTasks = tasks.filter((t) => {
    if (t.task_type === "loop" && !t.is_loop_occurrence) return false;
    if (t.is_loop_occurrence) {
      const occurrenceStartIso = t.start_date ? String(t.start_date).slice(0, 10) : "";
      const todayIso = new Date().toISOString().slice(0, 10);
      if (occurrenceStartIso > todayIso) return false;
    }
    if (t.deleted_at) return false;

    let matchesScope = false;

    const assigneeProfiles = getTaskAssigneeProfiles(t);
    const taskTeam = teams.find((tm) => tm.id === t.team_id);

    const scopeStr = filterScope || "all";

    if (scopeStr === "my") {
      matchesScope = isAssignedToCurrentUser(t);
    }
    else if (scopeStr === "team") {
      const inAdminDept = myAdminDeptIds.includes(t.department_id) || (taskTeam && myAdminDeptIds.includes(taskTeam.department_id));
      matchesScope = inAdminDept || (t.team_id && myAllowedTeamIds.includes(t.team_id)) ||
        assigneeProfiles.some((u) => myAllowedTeamIds.includes(u.team_id));
    }
    else if (scopeStr === "all") {
      if (isGlobalAdmin || isOperationalAdmin) {
        matchesScope = true;
      }
      else if (isDeptAdmin) {
        const inAdminDept = myAdminDeptIds.includes(t.department_id) || (taskTeam && myAdminDeptIds.includes(taskTeam.department_id));
        const inMyTeam = myAllowedTeamIds.includes(t.team_id) ||
          assigneeProfiles.some((u) => myAllowedTeamIds.includes(u.team_id));

        matchesScope = inAdminDept || inMyTeam;
      }
      else {
        matchesScope = isAssignedToCurrentUser(t) || myAllowedTeamIds.includes(t.team_id);
      }
    }
    else if (scopeStr.startsWith("team:")) {
      const specificTeamId = scopeStr.split(":")[1];
      matchesScope = t.team_id === specificTeamId;
    }
    else if (scopeStr.startsWith("department:")) {
      const specificDeptId = scopeStr.split(":")[1];
      const deptMatches = taskTeam && taskTeam.department_id === specificDeptId;
      matchesScope = deptMatches || t.department_id === specificDeptId;
    }

    const matchesStatus = filterStatus === "all" ? true : t.status === filterStatus;
    return matchesScope && matchesStatus && matchesSearch(t);
  });

  const dedupedMap = new Map();
  rawFilteredTasks.forEach(t => {
    if (t.is_loop_occurrence || t.task_type === "loop") {
      const parentId = t.loop_series_id || t.parent_task_id || t.id;
      const dateKey = t.start_date ? String(t.start_date).slice(0, 10) : "nodate";
      const key = `${parentId}_${dateKey}`;
      if (!dedupedMap.has(key)) {
        dedupedMap.set(key, t);
      }
    } else {
      dedupedMap.set(t.id, t);
    }
  });
  const filteredTasks = Array.from(dedupedMap.values());

  const handleExportCSV = () => {
    if (filteredTasks.length === 0) {
      showAlert("No tasks to export.");
      return;
    }

    const exportCols = ALL_EXPORT_COLUMNS.filter((c) =>
      selectedColumns.includes(c.id),
    );

    if (exportCols.length === 0) {
      showAlert("Please select at least one column.");
      return;
    }

    const headers = exportCols.map((c) => c.label);

    const csvContent = [
      headers.join(","),
      ...filteredTasks.map((t) => {
        const teamName =
          teams.find((team) => team.id === t.team_id)?.name || "";
        const subTeamName =
          subTeams.find((st) => st.id === t.sub_team_id)?.name || "";

        const row = exportCols.map((col) => {
          switch (col.id) {
            case "title":
              return t.title || "";
            case "assignee":
              return getAssigneeDisplay(t);
            case "assignee_email":
              return getTaskAssignees(t)
                .map((a) => a.email)
                .filter(Boolean)
                .join("; ");
            case "assigned_by": {
              return getCreatedByName(t);
            }
            case "start_date":
              return formatDate(t.start_date || t.startDate);
            case "due_date":
              return formatDate(t.due_date || t.dueDate);
            case "priority":
              return t.priority || "";
            case "status":
              return t.status || "";
            case "budget":
              return t.budget ? `$${Number(t.budget).toLocaleString()}` : "—";
            case "description":
              return (t.description || "")
                .replace(/,/g, " ")
                .replace(/\n/g, " ");
            case "hours":
              return t.hours || 0;
            case "team":
              return teamName;
            case "sub_team":
              return subTeamName;
            case "created_at":
              return formatDate(t.created_at);
            case "updated_at":
              return formatDate(t.updated_at);
            default:
              return "";
          }
        });

        return row.map((val) => `"${val}"`).join(",");
      }),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `tasks_export_${new Date().toISOString().slice(0, 10)}.csv`,
    );
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsExportModalOpen(false);
  };

  const TaskTableRenderer = (taskList) => (
    <div className="task-table-wrapper" style={{ overflowX: "auto", width: "100%" }}>
      <table
        className="modern-table"
        style={{
          tableLayout: "fixed",
          width: "100%",
          minWidth: "950px",
          borderCollapse: "separate",
          borderSpacing: "0 8px",
        }}
      >
        <thead>
          <tr style={{ background: "transparent" }}>
            {[
              ["Title", "17%"],
              ["Assignee", "12%"],
              ["Assigned By", "12%"],
              ["Start Date", "9%"],
              ["Due Date", "9%"],
              ["Budget", "8%"],
              ["Priority", "8%"],
              ["Status", "11%"],
              ["Actions", "14%"],
            ].map(([label, width]) => (
              <th
                key={label}
                style={{
                  width,
                  color: darkMode ? "#9ca3af" : "var(--gray-500)",
                  borderBottom: darkMode
                    ? "1px solid #374151"
                    : "1px solid var(--gray-200)",
                }}
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {taskList.map((t) => (
            <tr
              key={t.id}
              onClick={async () => {
                const fullTask = await loadTaskDetails(t.id);
                setSelectedTaskDetails(fullTask || t);
              }}
              style={{
                cursor: "pointer",
                border: darkMode
                  ? "1px solid #374151"
                  : "1px solid var(--gray-100)",
                backgroundColor: darkMode ? "#1f2937" : "#fff",
                transition: "all 0.2s",
              }}
            >
              <td
                style={{
                  fontWeight: 500,
                  color: darkMode ? "#f9fafb" : "var(--gray-900)",
                }}
              >
                {t.title}
              </td>

              <td style={{ color: darkMode ? "#d1d5db" : "var(--gray-600)" }}>
                {getAssigneeDisplay(t)}
              </td>

              <td style={{ color: darkMode ? "#d1d5db" : "var(--gray-600)" }}>
                {getCreatedByName(t)}
              </td>

              <td
                style={{
                  color: darkMode ? "#9ca3af" : "var(--gray-500)",
                  fontSize: 13,
                }}
              >
                {formatDate(t.start_date)}
              </td>

              <td
                style={{
                  color: darkMode ? "#9ca3af" : "var(--gray-500)",
                  fontSize: 13,
                }}
              >
                {formatDate(t.due_date)}
              </td>

              <td
                style={{
                  color: darkMode ? "#d1d5db" : "var(--gray-600)",
                  fontSize: 13,
                }}
              >
                {t.budget ? `$${Number(t.budget).toLocaleString()}` : "—"}
              </td>

              <td>
                <span
                  className={`priority-pill priority-${t.priority || "Medium"}`}
                  style={{
                    background:
                      t.priority === "High"
                        ? "#fee2e2"
                        : t.priority === "Low"
                          ? darkMode
                            ? "#1e3a24"
                            : "#f0fdf4"
                          : "#fef3c7",
                    color:
                      t.priority === "High"
                        ? "#b91c1c"
                        : t.priority === "Low"
                          ? darkMode
                            ? "#4ade80"
                            : "#166534"
                          : "#92400e",
                  }}
                >
                  {t.priority || "Medium"}
                </span>
              </td>

              <td>
                <select
                  className={`modern-select status-pill status-${(
                    t.status || ""
                  ).replace(" ", "")}`}
                  value={t.status || ""}
                  onChange={(e) => handleRowStatusChange(t, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  disabled={!canEditTask(t)}
                  style={{
                    cursor: canEditTask(t) ? "pointer" : "not-allowed",
                    opacity: canEditTask(t) ? 1 : 0.8,
                    width: "auto",
                    background:
                      t.status === "Completed"
                        ? darkMode
                          ? "#1e3a24"
                          : "#f0fdf4"
                        : t.status === "In Progress"
                          ? darkMode
                            ? "#1e293b"
                            : "#eef2ff"
                          : darkMode
                            ? "#374151"
                            : "#f9fafb",
                    color:
                      t.status === "Completed"
                        ? darkMode
                          ? "#4ade80"
                          : "#166534"
                        : t.status === "In Progress"
                          ? darkMode
                            ? "#818cf8"
                            : "#4338ca"
                          : darkMode
                            ? "#d1d5db"
                            : "#4b5563",
                    border: "none",
                    padding: "2px 8px",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: 600,
                  }}
                >
                  <option value="Todo">Todo</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                </select>
              </td>

              <td>
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  {getAttachments(t).length > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const atts = getAttachments(t);

                        if (atts.length === 1) {
                          if (isImageUrl(atts[0].url)) {
                            setSelectedImage(atts[0].url);
                          } else {
                            window.open(atts[0].url, "_blank");
                          }
                        } else {
                          setAttachmentsModalTask(t);
                        }
                      }}
                      title="View Attachment"
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#76AB3F",
                        padding: 4,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                      }}
                    >
                      👁
                    </button>
                  )}

                  {(isUser ||
                    (isDeptAdmin &&
                      isAssignedToCurrentUser(t))) &&
                    t.status !== "Completed" && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log("ADVANCE BUTTON CLICKED", t.id, t.status);
                          handleUserChangeStatus(t);
                        }}
                        className="btn-primary"
                        style={{ padding: "2px 6px", fontSize: 10 }}
                      >
                        Advance
                      </button>
                    )}

                  {(isGlobalAdmin ||
                    isOperationalAdmin ||
                    (isDeptAdmin && isTaskInMyTeam(t))) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRowDelete(t);
                        }}
                        className="btn-danger"
                        style={{ padding: "2px 6px", fontSize: 10 }}
                      >
                        Delete
                      </button>
                    )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const loadTaskDetails = async (taskId) => {
    const { data: taskData, error: taskError } = await supabase
      .from("tasks")
      .select("*, teams:team_id(id, name), sub_teams:sub_team_id(id, name)")
      .eq("id", taskId)
      .single();

    if (taskError) {
      console.error("Task details fetch failed:", taskError);
      return null;
    }

    const [
      assigneesRes,
      userCasesRes,
      issuesRes,
      extensionRes,
      notesRes,
      updatesRes,
    ] = await Promise.all([
      supabase
        .from("task_assignees")
        .select(`
          id,
          task_id,
          user_id,
          assigned_by,
          assigned_at,
          profiles:user_id (
            id,
            name,
            email,
            role,
            photo_url
          )
        `)
        .eq("task_id", taskId),

      supabase
        .from("task_user_cases")
        .select("*")
        .eq("task_id", taskId)
        .order("created_at", { ascending: false }),

      supabase
        .from("task_issues")
        .select("*, reporter:profiles!task_issues_reported_by_fkey(id, name, email)")
        .eq("task_id", taskId)
        .order("created_at", { ascending: false }),

      supabase
        .from("task_extension_requests")
        .select(`
          *,
          requester:profiles!task_extension_requests_requested_by_fkey(
            id,
            name,
            email
          )
        `)
        .eq("task_id", taskId)
        .order("created_at", { ascending: false }),

      supabase
        .from("task_notes")
        .select("*")
        .eq("task_id", taskId)
        .order("created_at", { ascending: false }),

      supabase
        .from("task_updates")
        .select("*")
        .eq("task_id", taskId)
        .order("created_at", { ascending: false }),
    ]);

    return {
      ...taskData,
      task_assignees: assigneesRes.data || [],
      task_user_cases: userCasesRes.data || [],
      task_issues: issuesRes.data || [],
      task_extension_requests: extensionRes.data || [],
      task_notes: notesRes.data || [],
      task_updates: updatesRes.data || [],
    };
  };

  if (selectedTaskDetails) {
    return (
      <TaskDetailsPage
        task={selectedTaskDetails}
        currentUser={currentUser}
        users={users}
        teams={teams}
        subTeams={subTeams}
        darkMode={darkMode}
        onBack={() => setSelectedTaskDetails(null)}
        onEdit={() => {
          navigate(`/tasks/${selectedTaskDetails.id}?edit=true`);
        }}
        onUploadFile={async ({ taskId, fileName, file }) => {
          if (!taskId || !file) return null;

          try {
            const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
            const filePath = `tasks/${taskId}/${Date.now()}_${safeName}`;

            const { error: uploadError } = await supabase.storage
              .from("attachments")
              .upload(filePath, file);

            if (uploadError) throw uploadError;

            const { data: publicUrlData } = supabase.storage
              .from("attachments")
              .getPublicUrl(filePath);

            const savedSupabaseFile = {
              name: fileName || file.name,
              url: publicUrlData.publicUrl,
              size: file.size,
              type: file.type || "file",
              uploaded_by: currentUser?.name || currentUser?.email || "User",
              uploaded_at: new Date().toISOString(),
              source: "Task",
            };

            const existingAttachments = Array.isArray(selectedTaskDetails.attachments)
              ? [...selectedTaskDetails.attachments]
              : [];

            const nextAttachments = [...existingAttachments, savedSupabaseFile];

            const { error: updateError } = await supabase
              .from("tasks")
              .update({
                attachments: nextAttachments,
                updated_at: new Date().toISOString(),
              })
              .eq("id", taskId);

            if (updateError) throw updateError;

            const refreshedTask = await loadTaskDetails(taskId);
            if (refreshedTask) setSelectedTaskDetails(refreshedTask);
            if (onRefreshTasks) await onRefreshTasks();

            return savedSupabaseFile;
          } catch (error) {
            console.error("File upload failed:", error);
            alert("File upload failed: " + error.message);
            return null;
          }
        }}
        onDeleteFiles={async (filesToDelete) => {
          if (!selectedTaskDetails?.id || !Array.isArray(filesToDelete) || filesToDelete.length === 0) return false;

          try {
            const urlsToDelete = filesToDelete.map((f) => f.url || f.publicUrl || f.public_url || f.file_url || f.download_url).filter(Boolean);

            // 1. Delete from Supabase Storage
            const pathsToDelete = urlsToDelete.map((url) => {
              const parts = url.split("/attachments/");
              return parts.length > 1 ? parts[1] : null;
            }).filter(Boolean);

            if (pathsToDelete.length > 0) {
              const { error: storageError } = await supabase.storage
                .from("attachments")
                .remove(pathsToDelete);

              if (storageError) {
                console.error("Storage delete failed:", storageError);
              }
            }

            // 2. Separate files by their source so we update the correct database tables
            const taskFiles = [];
            const issueFiles = [];
            const extensionFiles = [];
            const userCaseFiles = [];

            filesToDelete.forEach((file) => {
              const source = file.source;
              if (source === "Issue") {
                issueFiles.push(file);
              } else if (source === "Request Extension") {
                extensionFiles.push(file);
              } else if (source === "User Story") {
                userCaseFiles.push(file);
              } else {
                taskFiles.push(file);
              }
            });

            const promises = [];

            // A. Delete from tasks.attachments
            if (taskFiles.length > 0) {
              const taskUrls = taskFiles.map((f) => f.url || f.publicUrl || f.public_url || f.file_url || f.download_url).filter(Boolean);
              const existingAttachments = Array.isArray(selectedTaskDetails.attachments) ? selectedTaskDetails.attachments : [];
              const nextAttachments = existingAttachments.filter((att) => {
                const attUrl = att.url || att.publicUrl || att.public_url || att.file_url || att.download_url;
                return !taskUrls.includes(attUrl);
              });

              promises.push(
                supabase
                  .from("tasks")
                  .update({
                    attachments: nextAttachments,
                    updated_at: new Date().toISOString(),
                  })
                  .eq("id", selectedTaskDetails.id)
              );
            }

            // B. Delete from task_issues.attachments
            if (issueFiles.length > 0 && Array.isArray(selectedTaskDetails.task_issues)) {
              selectedTaskDetails.task_issues.forEach((issue) => {
                const issueUrls = issueFiles.map((f) => f.url || f.publicUrl || f.public_url || f.file_url || f.download_url).filter(Boolean);
                const existing = Array.isArray(issue.attachments) ? issue.attachments : [];
                const next = existing.filter((att) => {
                  const attUrl = att.url || att.publicUrl || att.public_url || att.file_url || att.download_url;
                  return !issueUrls.includes(attUrl);
                });

                if (existing.length !== next.length) {
                  promises.push(
                    supabase
                      .from("task_issues")
                      .update({ attachments: next })
                      .eq("id", issue.id)
                  );
                }
              });
            }

            // C. Delete from task_extension_requests.attachments
            if (extensionFiles.length > 0 && Array.isArray(selectedTaskDetails.task_extension_requests)) {
              selectedTaskDetails.task_extension_requests.forEach((req) => {
                const extUrls = extensionFiles.map((f) => f.url || f.publicUrl || f.public_url || f.file_url || f.download_url).filter(Boolean);
                const existing = Array.isArray(req.attachments) ? req.attachments : [];
                const next = existing.filter((att) => {
                  const attUrl = att.url || att.publicUrl || att.public_url || att.file_url || att.download_url;
                  return !extUrls.includes(attUrl);
                });

                if (existing.length !== next.length) {
                  promises.push(
                    supabase
                      .from("task_extension_requests")
                      .update({ attachments: next })
                      .eq("id", req.id)
                  );
                }
              });
            }

            // D. Delete from task_user_cases.attachments
            if (userCaseFiles.length > 0 && Array.isArray(selectedTaskDetails.task_user_cases)) {
              selectedTaskDetails.task_user_cases.forEach((uc) => {
                const ucUrls = userCaseFiles.map((f) => f.url || f.publicUrl || f.public_url || f.file_url || f.download_url).filter(Boolean);
                const existing = Array.isArray(uc.attachments) ? uc.attachments : [];
                const next = existing.filter((att) => {
                  const attUrl = att.url || att.publicUrl || att.public_url || att.file_url || att.download_url;
                  return !ucUrls.includes(attUrl);
                });

                if (existing.length !== next.length) {
                  promises.push(
                    supabase
                      .from("task_user_cases")
                      .update({ attachments: next })
                      .eq("id", uc.id)
                  );
                }
              });
            }

            const results = await Promise.all(promises);
            for (const res of results) {
              if (res.error) throw res.error;
            }

            const refreshedTask = await loadTaskDetails(selectedTaskDetails.id);
            if (refreshedTask) setSelectedTaskDetails(refreshedTask);
            if (onRefreshTasks) await onRefreshTasks();

            return true;
          } catch (error) {
            console.error("Failed to delete files:", error);
            alert("Failed to delete files: " + error.message);
            return false;
          }
        }}
        onAddExtension={async (payload) => {
          if (!selectedTaskDetails?.id) return false;

          const requestedDueDate = payload?.requestedDueDate;
          const reason = payload?.reason || "";
          const requestNote = payload?.requestNote || "";
          const files = payload?.files || [];

          if (!requestedDueDate?.trim()) {
            alert("Please select requested due date.");
            return false;
          }

          if (!reason.trim()) {
            alert("Please enter reason.");
            return false;
          }

          const uploadedAttachments = [];

          for (const file of files) {
            const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
            const filePath = `extension-requests/${selectedTaskDetails.id}/${Date.now()}_${safeName}`;

            const { error: uploadError } = await supabase.storage
              .from("attachments")
              .upload(filePath, file);

            if (uploadError) {
              alert("Extension file upload failed: " + uploadError.message);
              return false;
            }

            const { data } = supabase.storage.from("attachments").getPublicUrl(filePath);

            uploadedAttachments.push({
              name: file.name,
              url: data.publicUrl,
              size: `${Math.round(file.size / 1024)} KB`,
              type: file.type || "file",
              uploaded_at: new Date().toISOString(),
            });
          }

          const { error } = await supabase.from("task_extension_requests").insert({
            task_id: selectedTaskDetails.id,
            requested_due_date: requestedDueDate.trim(),
            reason: reason.trim(),
            request_note: requestNote.trim() || null,
            attachments: uploadedAttachments,
            impact: "Medium",
            status: "Pending",
            requested_by: currentUser?.id || null,
          });

          if (error) {
            alert("Failed to request extension: " + error.message);
            return false;
          }

          const { data: departmentAdmins, error: adminError } = await supabase
            .from("profiles")
            .select("id, name, email")
            .eq("team_id", selectedTaskDetails.team_id)
            .eq("role", "Department Admin")
            .eq("is_active", true)
            .is("deleted_at", null);

          if (adminError) {
            console.error("Department admin fetch failed:", adminError);
          }

          const admins = departmentAdmins || [];

          if (admins.length > 0) {
            await supabase.from("notifications").insert(
              admins.map((admin) => ({
                user_id: admin.id,
                task_id: selectedTaskDetails.id,
                title: selectedTaskDetails.title || "Task Extension Request",
                message: requestNote || reason || "Extension request submitted.",
                request_note: requestNote || reason || null,
                type: "TASK_EXTENSION_REQUEST",
                action_role: "department_admin",
                assignee_name: currentUser?.name || currentUser?.email || "Unknown User",
                assigned_to: currentUser?.name || currentUser?.email || "Unknown User",
                is_read: false,
                is_dismissed: false,
              }))
            );

            const emailAdmins = admins.filter((admin) => admin.email);

            console.log("Sending extension email to:", emailAdmins);

            if (emailAdmins.length > 0) {
              const response = await fetch("/api/email/extension-request", {
                method: "POST",
                headers: { "Content-Type": "application/json", "x-internal-token": process.env.REACT_APP_INTERNAL_TOKEN || "" },
                body: JSON.stringify({
                  users: emailAdmins,
                  task: {
                    id: selectedTaskDetails.id,
                    title: selectedTaskDetails.title,
                    description: selectedTaskDetails.description,
                    task_note: selectedTaskDetails.task_note,
                    assignee: getAssigneeDisplay(selectedTaskDetails) || currentUser?.name || "User",
                    assigned_to: getAssigneeDisplay(selectedTaskDetails) || currentUser?.name || "User",
                    assigned_by: currentUser?.name || currentUser?.email || "N/A",
                    priority: selectedTaskDetails.priority || "Medium",
                    start_date: selectedTaskDetails.start_date,
                    due_date: selectedTaskDetails.due_date,
                    requested_due_date: requestedDueDate.trim(),
                    reason: reason.trim(),
                    request_note: requestNote.trim(),
                    attachments: uploadedAttachments,
                  },
                  taskUrl: `${window.location.origin}/tasks/${selectedTaskDetails.id}?tab=extension`,
                }),
              });

              const result = await response.json();
              console.log("Extension email response:", result);

              if (!response.ok) {
                alert("Extension email failed: " + (result.error || "Unknown error"));
              }
            }
          }

          const refreshedTask = await loadTaskDetails(selectedTaskDetails.id);
          if (refreshedTask) setSelectedTaskDetails(refreshedTask);

          return true;
        }}
        onApproveExtension={async (requestId) => {
          const request = selectedTaskDetails?.task_extension_requests?.find((r) => r.id === requestId);

          if (!request?.requested_due_date) {
            alert("Requested due date not found.");
            return;
          }

          await supabase
            .from("task_extension_requests")
            .update({
              status: "Approved",
              reviewed_by: currentUser?.id || null,
              reviewed_at: new Date().toISOString(),
            })
            .eq("id", requestId);

          await supabase
            .from("tasks")
            .update({
              due_date: request.requested_due_date,
              updated_at: new Date().toISOString(),
            })
            .eq("id", selectedTaskDetails.id);

          const refreshedTask = await loadTaskDetails(selectedTaskDetails.id);
          if (refreshedTask) setSelectedTaskDetails(refreshedTask);
          if (onRefreshTasks) await onRefreshTasks();
        }}
        onRejectExtension={async (requestId) => {
          await supabase
            .from("task_extension_requests")
            .update({
              status: "Rejected",
              reviewed_by: currentUser?.id || null,
              reviewed_at: new Date().toISOString(),
            })
            .eq("id", requestId);

          const refreshedTask = await loadTaskDetails(selectedTaskDetails.id);
          if (refreshedTask) setSelectedTaskDetails(refreshedTask);
          if (onRefreshTasks) await onRefreshTasks();
        }}
        onAddIssue={async ({ title, description, priority = "Medium", files = [] }) => {
          if (!selectedTaskDetails?.id) return false;

          if (!title?.trim()) {
            alert("Please enter issue title.");
            return false;
          }

          const uploadedAttachments = [];

          for (const file of files) {
            const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
            const filePath = `task-issues/${selectedTaskDetails.id}/${Date.now()}_${safeName}`;

            const { error: uploadError } = await supabase.storage
              .from("attachments")
              .upload(filePath, file);

            if (uploadError) {
              alert("Issue file upload failed: " + uploadError.message);
              return false;
            }

            const { data } = supabase.storage.from("attachments").getPublicUrl(filePath);

            uploadedAttachments.push({
              name: file.name,
              url: data.publicUrl,
              size: `${Math.round(file.size / 1024)} KB`,
              type: file.type || "file",
              uploaded_at: new Date().toISOString(),
            });
          }

          const { error } = await supabase.from("task_issues").insert({
            task_id: selectedTaskDetails.id,
            title: title.trim(),
            description: description?.trim() || null,
            priority: priority || "Medium",
            status: "Open",
            reported_by: currentUser?.id || null,
            assigned_to: selectedTaskDetails?.task_assignees?.[0]?.user_id || null,
            attachments: uploadedAttachments,
          });

          if (error) {
            console.error("Issue insert failed:", error);
            alert("Failed to add issue: " + error.message);
            return false;
          }

          const refreshedTask = await loadTaskDetails(selectedTaskDetails.id);
          if (refreshedTask) setSelectedTaskDetails(refreshedTask);
          return true;
        }}
        onAdvanceIssue={async (issue) => {
          if (!issue?.id) return false;

          const nextStatus =
            issue.status === "Open"
              ? "In Progress"
              : issue.status === "In Progress"
              ? "Resolved"
              : "Resolved";

          const { error } = await supabase
            .from("task_issues")
            .update({ status: nextStatus })
            .eq("id", issue.id);

          if (error) {
            console.error("Issue status update failed:", error);
            alert("Failed to update issue: " + error.message);
            return false;
          }

          const refreshedTask = await loadTaskDetails(selectedTaskDetails.id);
          if (refreshedTask) setSelectedTaskDetails(refreshedTask);
          return true;
        }}
        onAdvanceUserCase={async (userCase) => {
          if (!userCase?.id) {
            alert("User case id missing.");
            return false;
          }

          const currentStatus = String(userCase.status || "Pending").trim().toLowerCase();

          const nextStatus =
            currentStatus === "pending"
              ? "Passed"
              : currentStatus === "passed"
              ? "In Review"
              : "In Review";

          const { data, error } = await supabase
            .from("task_user_cases")
            .update({ status: nextStatus })
            .eq("id", userCase.id)
            .select("id, status")
            .single();

          if (error) {
            alert("Failed to update user case: " + error.message);
            return false;
          }

          alert(`User case moved to ${data.status}`);

          const refreshedTask = await loadTaskDetails(selectedTaskDetails.id);
          if (refreshedTask) setSelectedTaskDetails(refreshedTask);

          return true;
        }}
        onAddUserCase={async ({
          title,
          description,
          acceptanceCriteria,
          testCases = "",
          files = [],
        }) => {
          if (!selectedTaskDetails?.id) return false;

          if (!title?.trim()) {
            alert("Please enter user case title.");
            return false;
          }

          const parsedTestCases = (Array.isArray(testCases) ? testCases : String(testCases || "").split("\n"))
            .map((line) => line.trim())
            .filter(Boolean)
            .map((line) => ({ title: line, status: "Pending" }));

          const uploadedAttachments = [];

          for (const file of files) {
            const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
            const filePath = `user-cases/${selectedTaskDetails.id}/${Date.now()}_${safeName}`;

            const { error: uploadError } = await supabase.storage
              .from("attachments")
              .upload(filePath, file);

            if (uploadError) {
              alert("User case file upload failed: " + uploadError.message);
              return false;
            }

            const { data } = supabase.storage
              .from("attachments")
              .getPublicUrl(filePath);

            uploadedAttachments.push({
              name: file.name,
              url: data.publicUrl,
              size: `${Math.round(file.size / 1024)} KB`,
              type: file.type || "file",
              uploaded_at: new Date().toISOString(),
            });
          }

          const { error } = await supabase.from("task_user_cases").insert({
            task_id: selectedTaskDetails.id,
            title: title.trim(),
            description: description?.trim() || null,
            acceptance_criteria: acceptanceCriteria?.trim() || null,
            status: "Pending",
            test_cases: parsedTestCases,
            test_cases_count: parsedTestCases.length,
            attachments: uploadedAttachments,
            created_by: currentUser?.id || null,
          });

          if (error) {
            console.error("User case insert failed:", error);
            alert("Failed to add user case: " + error.message);
            return false;
          }

          const refreshedTask = await loadTaskDetails(selectedTaskDetails.id);
          if (refreshedTask) setSelectedTaskDetails(refreshedTask);

          return true;
        }}
        onAddAssignee={async (user) => {
          if (!selectedTaskDetails?.id || !user?.id) return false;

          const finalUser =
            users.find((u) => u.id === user.id) ||
            users.find((u) => u.email?.toLowerCase() === user.email?.toLowerCase()) ||
            user;

          const taskStartDate =
            selectedTaskDetails.start_date ||
            selectedTaskDetails.repeat_start_date ||
            selectedTaskDetails.startDate ||
            null;

          const taskDueDate =
            selectedTaskDetails.due_date ||
            selectedTaskDetails.dueDate ||
            null;

          const { error: assigneeError } = await supabase.from("task_assignees").upsert(
            {
              task_id: selectedTaskDetails.id,
              user_id: finalUser.id,
              assigned_by: currentUser?.id || null,
            },
            { onConflict: "task_id,user_id" }
          );

          if (assigneeError) {
            alert("Failed to add assignee: " + assigneeError.message);
            return false;
          }

          const { error: notificationError } = await supabase
            .from("notifications")
            .insert({
              user_id: finalUser.id,
              task_id: selectedTaskDetails.id,
              title: "Task assigned",
              message:
                selectedTaskDetails.task_note ||
                `You have been assigned "${selectedTaskDetails.title}"`,
              type: "TASK_CREATED",
              action_role: "assignee",
              assignee_name: finalUser.name || finalUser.email,
              assigned_to: finalUser.name || finalUser.email,
              attachments: selectedTaskDetails.attachments || [],
              is_read: false,
              is_dismissed: false,
            });

          if (notificationError) {
            alert("Assignee added, but notification failed: " + notificationError.message);
            return false;
          }

          if (finalUser.email) {
            const response = await fetch("/api/email/task-created", {
              method: "POST",
              headers: { "Content-Type": "application/json", "x-internal-token": process.env.REACT_APP_INTERNAL_TOKEN || "" },
              body: JSON.stringify({
                users: [
                  {
                    id: finalUser.id,
                    name: finalUser.name || finalUser.email,
                    email: finalUser.email,
                  },
                ],
                task: {
                  id: selectedTaskDetails.id,
                  title: selectedTaskDetails.title,
                  description:
                    selectedTaskDetails.task_note || selectedTaskDetails.description,
                  assigned_to: finalUser.name || finalUser.email,
                  assigned_by: currentUser?.name || currentUser?.email || "N/A",
                  start_date: taskStartDate,
                  due_date: taskDueDate,
                  priority: selectedTaskDetails.priority || "Medium",
                  task_type: selectedTaskDetails.task_type,
                  repeat_frequency: selectedTaskDetails.repeat_frequency,
                  repeat_every: selectedTaskDetails.repeat_every,
                  attachments: selectedTaskDetails.attachments || [],
                },
                taskUrl: `${window.location.origin}/tasks/${selectedTaskDetails.id}`,
              }),
            });

            const result = await response.json();
            console.log("Loop add-assignee email response:", result);

            if (!response.ok) {
              alert("Assignee added, but email failed: " + (result.error || "Unknown error"));
            }
          }

          const refreshedTask = await loadTaskDetails(selectedTaskDetails.id);
          if (refreshedTask) setSelectedTaskDetails(refreshedTask);
          if (onRefreshTasks) await onRefreshTasks();

          alert("Assignee added successfully.");
          return true;
        }}
        onDeleteAssignees={async ({ taskId, userIds }) => {
          const { error } = await supabase
            .from("task_assignees")
            .delete()
            .eq("task_id", taskId)
            .in("user_id", userIds);

          if (error) {
            console.error(error);
            return false;
          }

          const refreshedTask = await loadTaskDetails(taskId);
          if (refreshedTask) setSelectedTaskDetails(refreshedTask);
          if (onRefreshTasks) await onRefreshTasks();
          return true;
        }}
        onDeleteTask={async (taskId, deleteSeries = false) => {
          try {
            await onDeleteTask(taskId, deleteSeries);
            if (onRefreshTasks) await onRefreshTasks();
            return true;
          } catch (err) {
            console.error("Delete task failed:", err);
            return false;
          }
        }}
        onAddUpdate={async (text) => {
          if (!text?.trim()) return;

          const { error } = await supabase.from("task_updates").insert({
            task_id: selectedTaskDetails.id,
            user_id: currentUser?.id || null,
            update_text: text.trim(),
            progress_percent: 0,
          });

          if (error) {
            console.error("Task update insert failed:", error);
            showAlert("Failed to add update: " + error.message);
          } else {
            showAlert("Update added successfully.");
          }
        }}
      />
    );
  }

  return (
    <div className="page">
      <div
        className="task-board-header"
        style={{
          background: darkMode ? "#4d7a22" : "#76AB3F",
          padding: "28px 34px",
          borderRadius: "30px",
          color: "white",
          boxShadow: darkMode
            ? "0 10px 25px -5px rgba(0,0,0,0.4)"
            : "0 10px 25px -5px rgba(16,185,129,0.25)",
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "nowrap",
          overflow: "visible",
          marginBottom: 24,
          minHeight: 88,
        }}
      >
        <h2
          style={{
            color: "white",
            margin: 0,
            fontWeight: 900,
            fontSize: "20px",
            whiteSpace: "nowrap",
            flexShrink: 0,
            letterSpacing: "0.0px",
          }}
        >
          Task Board
        </h2>

        {!isUser && (
          <div className="task-board-select-wrapper" style={{ width: 140, flexShrink: 0 }}>
            <CustomSelect
              options={(() => {
                if (isGlobalAdmin || isOperationalAdmin) {
                  return [
                    { value: "all", label: "All Tasks" },
                    { value: "my", label: "My Tasks" },
                    ...teams.map((t) => ({ value: `team:${t.id}`, label: t.name })),
                  ];
                }
                if (isDeptAdmin && totalAssignedTeams > 1) {
                  return [
                    { value: "all", label: "All Tasks" },
                    { value: "my", label: "My Tasks" },
                    ...assignedTeams.map((t) => ({ value: `team:${t.id}`, label: t.name })),
                  ];
                }
                if (isDeptAdmin && totalAssignedTeams <= 1) {
                  return [
                    { value: "team", label: "Team Tasks" },
                    { value: "my", label: "My Tasks" },
                  ];
                }
                return [];
              })()}
              value={filterScope}
              onChange={(val) => {
                setFilterScope(val);
                setFilterStatus("all");
              }}
              darkMode={darkMode}
            />
          </div>
        )}

        <div
          className="task-board-status-buttons"
          style={{
            display: "flex",
            gap: 9,
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          {["Todo", "In Progress", "Completed"].map((status) => (
            <button
              key={status}
              onClick={() =>
                setFilterStatus((prev) => (prev === status ? "all" : status))
              }
              style={{
                background:
                  filterStatus === status ? "white" : "rgba(255,255,255,0.72)",
                color: "#5f9e2f",
                border: "none",
                borderRadius: "9px",
                padding: "0 20px",
                fontWeight: 800,
                fontSize: "10px",
                cursor: "pointer",
                whiteSpace: "nowrap",
                minWidth: status === "In Progress" ? 40 : 20,
                height: 52,
                boxShadow:
                  filterStatus === status
                    ? "0 3px 8px rgba(0,0,0,0.12)"
                    : "none",
              }}
            >
              {status}
            </button>
          ))}
        </div>

        <div className="task-board-spacer" style={{ flex: 1 }} />

        <div
          className="task-board-search-wrapper"
          style={{
            position: "relative",
            width: 150,
            height: 40,
            flexShrink: 0,
          }}
        >
          <input
            id="task-list-search-tasks"
            name="searchTasks"
            placeholder="Search tasks..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "100%",
              height: "100%",
              background: "rgba(255,255,255,0.24)",
              border: "none",
              color: "white",
              padding: "0 15px",
              borderRadius: "9px",
              outline: "none",
              fontSize: "10px",
              fontWeight: 500,
              boxSizing: "border-box",
            }}
            className="header-search-input"
          />
        </div>

        <style>{`
          .header-search-input::placeholder {
            color: rgba(255,255,255,0.88);
          }
        `}</style>

        {canExport && (
          <button
            onClick={() => setIsExportModalOpen(true)}
            className="task-board-export-btn"
            style={{
              background: "white",
              color: "#5f9e2f",
              border: "none",
              borderRadius: "10px",
              padding: "0 30px",
              height: 40,
              fontWeight: 800,
              fontSize: "10px",
              cursor: "pointer",
              whiteSpace: "nowrap",
              flexShrink: 0,
              boxShadow: "0 3px 8px rgba(0,0,0,0.12)",
            }}
          >
            Export CSV
          </button>
        )}

        {canCreate && (
          <button
            onClick={() => navigate("/tasks/new")}
            className="task-board-create-btn"
            style={{
              background: "white",
              color: "#5f9e2f",
              border: "none",
              borderRadius: "16px",
              padding: "0 30px",
              height: 34,
              fontWeight: 800,
              fontSize: "10px",
              cursor: "pointer",
              whiteSpace: "nowrap",
              display: "flex",
              alignItems: "center",
              gap: 5,
              flexShrink: 0,
              boxShadow: "0 3px 8px rgba(0,0,0,0.12)",
            }}
          >
            <span style={{ fontSize: 24, lineHeight: 1 }}>+</span>
            New Task
          </button>
        )}
      </div>

      <div
        className="task-list-vertical"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 32,
          marginTop: 24,
        }}
      >
        {filterStatus !== "all" ? (
          <div className="task-section">
            <div className="task-section-header" style={{ marginBottom: 16 }}>
              <h3
                style={{
                  fontSize: 18,
                  color: darkMode ? "#f9fafb" : "var(--gray-800)",
                }}
              >
                {filterStatus} ({filteredTasks.length})
              </h3>
            </div>

            {filteredTasks.length === 0 ? (
              <div
                style={{
                  fontStyle: "italic",
                  color: darkMode ? "#9ca3af" : "var(--gray-500)",
                  fontSize: 14,
                }}
              >
                No tasks found.
              </div>
            ) : (
              TaskTableRenderer(
                filterStatus === "Completed"
                  ? [...filteredTasks].sort((a, b) => {
                      const getSortDate = (t) => {
                        const due = t.due_date || t.dueDate;
                        const start = t.start_date || t.startDate;
                        const created = t.created_at;
                        return new Date(due || start || created || 0).getTime();
                      };
                      return getSortDate(b) - getSortDate(a);
                    })
                  : filteredTasks
              )
            )}
          </div>
        ) : (
          <>
            {columns
              .filter((c) => c.key === "In Progress" || c.key === "Todo")
              .sort((a, b) => {
                if (a.key === "In Progress") return -1;
                if (b.key === "In Progress") return 1;
                return 0;
              })
              .map((col) => {
                const sectionTasks = filteredTasks.filter((t) => {
                  const raw = (t.status || "").toString().trim().toLowerCase();
                  let norm = "todo";

                  if (raw === "in progress") norm = "in progress";
                  else if (raw === "completed") norm = "completed";

                  return norm === col.key.toLowerCase();
                });

                return (
                  <div key={col.key} className="task-section">
                    <div
                      className="task-section-header"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        marginBottom: 16,
                        paddingBottom: 12,
                        borderBottom: darkMode
                          ? "1px solid #374151"
                          : "2px solid var(--gray-200)",
                      }}
                    >
                      <h3
                        style={{
                          fontSize: 18,
                          color: darkMode ? "#f9fafb" : "var(--gray-800)",
                        }}
                      >
                        {col.label}
                      </h3>

                      <span
                        style={{
                          background: darkMode ? "#374151" : "var(--gray-200)",
                          padding: "2px 10px",
                          borderRadius: 12,
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {sectionTasks.length}
                      </span>
                    </div>

                    {sectionTasks.length === 0 ? (
                      <div
                        style={{
                          fontStyle: "italic",
                          color: darkMode ? "#9ca3af" : "var(--gray-500)",
                          fontSize: 14,
                          paddingLeft: 8,
                        }}
                      >
                        No tasks
                      </div>
                    ) : (
                      TaskTableRenderer(
                        col.key === "Completed"
                          ? [...sectionTasks].sort((a, b) => {
                              const getSortDate = (t) => {
                                const due = t.due_date || t.dueDate;
                                const start = t.start_date || t.startDate;
                                const created = t.created_at;
                                return new Date(due || start || created || 0).getTime();
                              };
                              return getSortDate(b) - getSortDate(a);
                            })
                          : sectionTasks
                      )
                    )}
                  </div>
                );
              })}

            <div className="task-section">
              <button
                onClick={() => setShowCompleted(!showCompleted)}
                style={{
                  width: "100%",
                  padding: "16px",
                  background: darkMode ? "#1f2937" : "var(--gray-100)",
                  border: darkMode
                    ? "2px dashed #374151"
                    : "2px dashed var(--gray-300)",
                  borderRadius: "12px",
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  marginBottom: 16,
                }}
              >
                {showCompleted
                  ? "Hide Completed Tasks"
                  : `Show Completed Tasks (${filteredTasks.filter(
                    (t) =>
                      (t.status || "").toString().trim().toLowerCase() ===
                      "completed",
                  ).length
                  })`}
                <span
                  style={{
                    transform: showCompleted
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: "transform 0.2s",
                  }}
                >
                  ▼
                </span>
              </button>

              {showCompleted && (
                <div>
                  {(() => {
                    const completedTasks = filteredTasks.filter(
                      (t) =>
                        (t.status || "").toString().trim().toLowerCase() ===
                        "completed",
                    );

                    completedTasks.sort((a, b) => {
                      const getSortDate = (t) => {
                        const due = t.due_date || t.dueDate;
                        const start = t.start_date || t.startDate;
                        const created = t.created_at;
                        return new Date(due || start || created || 0).getTime();
                      };
                      return getSortDate(b) - getSortDate(a);
                    });

                    return completedTasks.length === 0 ? (
                      <div
                        style={{
                          fontStyle: "italic",
                          color: darkMode ? "#9ca3af" : "var(--gray-500)",
                          fontSize: 14,
                          textAlign: "center",
                          padding: 20,
                        }}
                      >
                        No completed tasks found.
                      </div>
                    ) : (
                      TaskTableRenderer(completedTasks)
                    );
                  })()}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {isModalOpen && (
        <TaskForm
          onSave={handleSave}
          onClose={() => {
            setIsModalOpen(false);
            setEditingTask(null);
          }}
          users={users}
          currentUser={currentUser}
          initial={editingTask}
          readOnly={false}
          allowMainEditing={
            !editingTask
              ? isGlobalAdmin || isOperationalAdmin || isDeptAdmin || isUser
              : isGlobalAdmin ||
              isOperationalAdmin ||
              (isDeptAdmin && isTaskInMyTeam(editingTask))
          }
          onRefreshUsers={onRefreshUsers}
          teams={teams}
          subTeams={subTeams}
          darkMode={darkMode}
        />
      )}

      {deleteConfirmationTask && (
        <div className="modal-backdrop" onClick={cancelDelete}>
          <div
            className="modal-card"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 400 }}
          >
            <div className="modal-header">
              <h3>
                {deleteConfirmationTask.is_loop_occurrence || deleteConfirmationTask.task_type === "loop" 
                  ? "Delete Loop Task" 
                  : "Confirm Delete"}
              </h3>
              <button className="modal-close" onClick={cancelDelete}>
                ✕
              </button>
            </div>

            <div>
              {deleteConfirmationTask.is_loop_occurrence || deleteConfirmationTask.task_type === "loop" ? (
                <>
                  <p>
                    Do you want to delete only this occurrence or the entire loop series?
                  </p>
                  <div className="modal-actions-row" style={{ marginTop: 24 }}>
                    <button className="btn-secondary" onClick={cancelDelete}>
                      Cancel
                    </button>
                    <button 
                      className="btn-danger" 
                      onClick={() => {
                        onDeleteTask(deleteConfirmationTask.id, false);
                        setDeleteConfirmationTask(null);
                      }}
                    >
                      Delete This Task Only
                    </button>
                    <button 
                      className="btn-danger" 
                      onClick={() => {
                        onDeleteTask(deleteConfirmationTask.id, true);
                        setDeleteConfirmationTask(null);
                      }}
                    >
                      Delete Whole Loop Series
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p>
                    Are you sure you want to delete task{" "}
                    <strong>{deleteConfirmationTask.title}</strong>?
                  </p>

                  <p style={{ fontSize: 13, marginTop: 8 }}>
                    This action will move the task to the recycle bin.
                  </p>

                  <div className="modal-actions-row">
                    <button className="btn-secondary" onClick={cancelDelete}>
                      Cancel
                    </button>

                    <button className="btn-danger" onClick={confirmDelete}>
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {advanceConfirmation && (
        <div className="modal-backdrop" onClick={cancelAdvance}>
          <div
            className="modal-card"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 400 }}
          >
            <div className="modal-header">
              <h3>Confirm Advance</h3>
              <button className="modal-close" onClick={cancelAdvance}>
                ✕
              </button>
            </div>

            <div>
              <p>
                This task will move from <strong>{advanceConfirmation.task.status}</strong> to <strong>{advanceConfirmation.nextStatus}</strong>. Do you want to continue?
              </p>

              <div className="modal-actions-row" style={{ marginTop: 24 }}>
                <button className="btn-secondary" onClick={cancelAdvance}>
                  Cancel
                </button>
                <button className="btn-primary" onClick={confirmAdvance}>
                  Confirm Advance
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {attachmentsModalTask && (
        <div
          className="modal-backdrop"
          onClick={() => setAttachmentsModalTask(null)}
        >
          <div
            className="modal-card"
            style={{
              maxWidth: 450,
              width: "100%",
              borderRadius: "16px",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="modal-header"
              style={{
                background: "linear-gradient(135deg, #76AB3F 0%, #5F9E3F 100%)",
                padding: "20px 24px",
              }}
            >
              <h3
                style={{
                  color: "white",
                  fontSize: "18px",
                  fontWeight: 700,
                  margin: 0,
                }}
              >
                Attachments
              </h3>

              <button
                className="modal-close"
                onClick={() => setAttachmentsModalTask(null)}
                style={{ color: "white", opacity: 0.9 }}
              >
                ✕
              </button>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                padding: "24px",
                maxHeight: "60vh",
                overflowY: "auto",
                background: "#f9fafb",
              }}
            >
              {getAttachments(attachmentsModalTask).map((att, i) => {
                const isImage = att.url.match(/\.(jpg|jpeg|png|gif|webp)$/i);

                return (
                  <a
                    key={i}
                    href={att.url}
                    target={isImage ? undefined : "_blank"}
                    rel="noreferrer"
                    onClick={(e) => {
                      if (isImage) {
                        e.preventDefault();
                        setSelectedImage(att.url);
                      }
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "16px",
                      background: "white",
                      borderRadius: "12px",
                      textDecoration: "none",
                      color: "var(--gray-800)",
                      fontWeight: 500,
                      border: "1px solid #e5e7eb",
                      flexDirection: isImage ? "column" : "row",
                      gap: isImage ? "12px" : "16px",
                    }}
                  >
                    {isImage && (
                      <div
                        style={{
                          width: "100%",
                          height: "150px",
                          borderRadius: "8px",
                          overflow: "hidden",
                          background: "#f3f4f6",
                        }}
                      >
                        <img
                          src={att.url}
                          alt={att.name || `Attachment ${i + 1}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                    )}

                    <div style={{ overflow: "hidden", width: "100%" }}>
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#1f2937",
                          display: "block",
                        }}
                      >
                        {att.name || `Attachment ${i + 1}`}
                      </span>

                      <span
                        style={{
                          fontSize: "12px",
                          color: "#9ca3af",
                          marginTop: "2px",
                        }}
                      >
                        Click to view
                      </span>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {isExportModalOpen && (
        <div
          className="modal-backdrop"
          onClick={() => setIsExportModalOpen(false)}
        >
          <div
            className="modal-card"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 500 }}
          >
            <div className="modal-header">
              <h3>Export Columns</h3>
              <button
                className="modal-close"
                onClick={() => setIsExportModalOpen(false)}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: "10px 0" }}>
              <p style={{ marginBottom: 16, fontSize: 14 }}>
                Select the columns you want to include in your CSV export:
              </p>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px 24px",
                  maxHeight: "400px",
                  overflowY: "auto",
                  padding: "4px",
                }}
              >
                {ALL_EXPORT_COLUMNS.map((col) => (
                  <label
                    key={col.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      cursor: "pointer",
                      fontSize: 14,
                    }}
                  >
                    <input
                      id={`column-checkbox-${col.id}`}
                      name={`columnCheckbox-${col.id}`}
                      type="checkbox"
                      checked={selectedColumns.includes(col.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedColumns((prev) => [...prev, col.id]);
                        } else {
                          setSelectedColumns((prev) =>
                            prev.filter((id) => id !== col.id),
                          );
                        }
                      }}
                      style={{ width: 16, height: 16, cursor: "pointer" }}
                    />
                    {col.label}
                  </label>
                ))}
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 12,
                  marginTop: 24,
                  borderTop: "1px solid var(--gray-100)",
                  paddingTop: 16,
                }}
              >
                <button
                  className="btn-secondary"
                  style={{ fontSize: 13, padding: "6px 12px" }}
                  onClick={() =>
                    setSelectedColumns(ALL_EXPORT_COLUMNS.map((c) => c.id))
                  }
                >
                  Select All
                </button>

                <button
                  className="btn-secondary"
                  style={{ fontSize: 13, padding: "6px 12px" }}
                  onClick={() => setSelectedColumns([])}
                >
                  Deselect All
                </button>

                <div style={{ flex: 1 }} />

                <button
                  className="btn-secondary"
                  onClick={() => setIsExportModalOpen(false)}
                >
                  Cancel
                </button>

                <button className="btn-primary" onClick={handleExportCSV}>
                  Export CSV
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedImage && (
        <div
          className="modal-backdrop"
          onClick={() => setSelectedImage(null)}
          style={{
            zIndex: 2000,
            background: "rgba(0,0,0,0.85)",
            backdropFilter: "blur(8px)",
          }}
        >
          <div
            style={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedImage(null)}
              style={{
                position: "absolute",
                top: -40,
                right: 0,
                background: "white",
                color: "#1f2937",
                border: "none",
                borderRadius: "50%",
                width: 32,
                height: 32,
                cursor: "pointer",
              }}
            >
              ✕
            </button>

            <div
              style={{
                background: "white",
                padding: "12px",
                borderRadius: "20px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src={selectedImage}
                alt="Task Attachment Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "calc(90vh - 24px)",
                  objectFit: "contain",
                  borderRadius: "12px",
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TaskList;